import gameScreenCover from '@assets/images/game-screen-cover.png';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@/components/Loading';
import { RelatedDisplay } from '@/components/RelatedDisplay';
import { useGameEngine } from '@/hooks/useGameEngine';
import { useAuthStore } from '@/store/authStore';
import { useBgmStore } from '@/store/bgmStore';
import { useGameStore } from '@/store/gameStore';
import { useSeStore } from '@/store/seStore';

function GamePage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { setScore, resetGame, saveScore, setPendingSave, clearPendingSave } =
    useGameStore();
  const { play, pause, isPlaying, isBgmEnabled } = useBgmStore();
  const [canStart, setCanStart] = useState(false);

  const { engineRef: gameEngineRef, isLoading } = useGameEngine({
    containerRef,
    isEnabled: canStart,
    enableBlockMerge: true,
    onGameEnd: (finalScore, blockCounts) => {
      setScore(finalScore);
      saveScore();
      setPendingSave({ finalScore, blockCounts });
      navigate('/results');
    },
  });

  useEffect(() => {
    const handler = () => {
      gameEngineRef.current?.endGame();
    };
    window.addEventListener('requestGameEnd', handler);
    return () => {
      window.removeEventListener('requestGameEnd', handler);
    };
  }, [gameEngineRef]);

  const initGame = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;

    const userId = await useAuthStore.getState().ensureValidUserId();
    if (!userId) {
      navigate('/', { replace: true });
      setCanStart(false);
      return;
    }

    resetGame();
    clearPendingSave();
    setCanStart(true);

    const { seVolume, setSeVolume } = useSeStore.getState();
    setSeVolume(seVolume);
  }, [navigate, resetGame, clearPendingSave]);

  useEffect(() => {
    initGame();
    return () => {
      setCanStart(false);
    };
  }, [initGame]);

  useEffect(() => {
    if (!isBgmEnabled) {
      pause();
      return;
    }

    if (!isPlaying()) {
      play(2000);
    }
  }, [isBgmEnabled, pause, play, isPlaying]);

  return (
    <div className="relative h-svh overflow-clip flex items-center justify-center z-0">
      {isLoading && <Loading />}
      <RelatedDisplay />
      <div className="relative">
        <div
          ref={containerRef}
          id="game-container"
          className="touch-none select-none grid place-items-center md:h-full [&>canvas]:aspect-4/7 [&>canvas]:w-[90%] md:[&>canvas]:w-full"
        />
      </div>
      <div
        className="z-[-1] absolute inset-0 size-full object-cover pointer-events-none select-none"
        style={{ animation: 'fadeIn 4s ease-out' }}
      >
        <img
          src={gameScreenCover}
          alt=""
          className="size-full object-cover opacity-95"
        />
      </div>
    </div>
  );
}

export default GamePage;
